import { z } from 'zod';
import { CUSTOM_PROPERTY_ENTITIES } from '../constants.js';
import { deletedFilterFields, refineDeletedFilterMutualExclusion } from './common.js';
export const CustomPropertyTypeSchema = z.enum([
    'text', 'number', 'date', 'datetime', 'boolean', 'select', 'document_link', 'url', 'user',
]);
export const CustomPropertyEntitySchema = z.enum(CUSTOM_PROPERTY_ENTITIES);
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const CustomPropertyCreateSchema = z.object({
    name: z.string().min(1).max(200),
    key: z.string().min(1).max(64).regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits and underscores; must start with a letter').optional(),
    type: CustomPropertyTypeSchema,
    description: z.string().max(1000).nullable().optional(),
    required: z.boolean().default(false),
    options: z.array(z.string()).default([]),
    /** Which entity this property attaches to. Defaults to 'document' for
     *  backwards compatibility with the existing UI; pass 'work_item' to
     *  attach it to work items. */
    entity: CustomPropertyEntitySchema.default('document'),
    /** 0.1.11 — scope the property to documents (or work-items) that
     *  carry at least one of the listed tag names. Empty array (default)
     *  means org-wide: every entity of the matching `entity` type sees
     *  the property. Non-empty means "only show when relevant", which
     *  is what tool-installed schemas should use to avoid leaking onto
     *  unrelated documents. The check is by tag NAME, not id, so it
     *  survives tag-name renames that go through the proper migration. */
    appliesToTags: z.array(z.string().min(1).max(200)).max(50).default([]),
    /** 0.1.30 — Stage 4a of the Document Types FR. Parallel-installed
     *  alongside `appliesToTags`. When non-empty, this array is the
     *  AUTHORITATIVE filter — matched against the doc's `baselineType.id`
     *  (e.g. 'html-doc', 'crawl-page'). Empty means "type-binding not
     *  declared" and the runtime falls back to `appliesToTags`. Values
     *  should match the kebab-case ids in @captain-tasko/shared/document-
     *  types.ts. Stored as text[] in Postgres — no FK; the registry
     *  stays in TypeScript so new types ship without a migration. */
    appliesToTypes: z.array(z.string().min(1).max(100)).max(50).default([]),
    /** 0.1.17 — optional pointer for the project_to_custom_properties
     *  workflow action to project values from sidecar.frontmatter into
     *  documents.customProperties at import time. Null = operator-edit-
     *  only (legacy behavior). Forward-compat: only 'frontmatter' is
     *  recognized today; 'document' and 'computed' join later without
     *  another schema change. */
    source: z.object({
        from: z.literal('frontmatter'),
        /** Dotted path into sidecar.frontmatter. Same semantics as the
         *  tagging-rule engine's getValueByPath (e.g.
         *  'crawl_job.duration_s'). */
        path: z.string().min(1).max(200),
    }).nullable().optional(),
}).strict();
// .strip() restores Zod's default unknown-key tolerance for PATCH —
// CTSK-445 made Create strict, and .partial() would otherwise inherit
// `unknownKeys: "strict"`. Patch semantics deliberately unchanged.
export const CustomPropertyPatchSchema = CustomPropertyCreateSchema.partial().strip();
// CTSK-388 (Phase 2 of CTSK-385) — first list-query schema for custom
// properties. `GET /custom-properties` previously read no query params
// at all and returned soft-deleted rows unconditionally; this closes
// that gap.
export const CustomPropertiesListQuerySchema = z
    .object({
    ...deletedFilterFields,
})
    .superRefine(refineDeletedFilterMutualExclusion);
//# sourceMappingURL=custom-property.js.map